import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from '../../ui/Spinner';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { mergeCustomers } from '../adminApi';
import { useCustomers } from './useCustomers';
import { errorMessage } from '../../core/errors';
import { duplicateCandidates, duplicateReasonLabel } from '../../core/rules/customerMatch';
import { contactHint, customerName, customerSearchText } from './customerRules';
import type { Account } from '../../core/types/account';
import type { CustomerFile, CustomerSummary } from '../../core/types/adminRequests';
type Props = {
  open: boolean;
  onClose: () => void;
  /** Het dossier dat open staat: dit blijft. */
  account: Account;
  onMerged: (file: CustomerFile) => void;
};
const MAX_RESULTS = 6;

/**
 * "Samenvoegen met…": een dubbel dossier (bijv. uit Salonized, zonder e-mail) gaat op in dit dossier.
 * Mogelijke dubbelen staan bovenaan. Na kiezen eerst een bevestiging: het andere dossier verdwijnt
 * en dat is niet terug te draaien. Of het mag (twee logins, twee kuren) beslist de server.
 */
export function MergeCustomerDialog({
  open,
  onClose,
  account,
  onMerged
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    customers,
    status
  } = useCustomers(open);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<CustomerSummary | null>(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const c = account.customer;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setQuery('');
      setPicked(null);
      setProblem('');
      d.showModal();
      searchRef.current?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);
  const suggestions = useMemo(() => duplicateCandidates(customers, c, {
    exceptId: c.id
  }).slice(0, 3), [customers, c]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? customers.filter(x => x.id !== c.id && customerSearchText(x).includes(q)).slice(0, MAX_RESULTS) : [];
  }, [customers, query, c.id]);
  const merge = async () => {
    if (!picked || lock.current) return;
    lock.current = true;
    setBusy(true);
    setProblem('');
    try {
      const file = await mergeCustomers(c.id, picked.id);
      toast.success(`${customerName(picked)} is samengevoegd met dit dossier.`);
      onMerged(file);
      onClose();
    } catch (err) {
      // 409 (twee logins, twee kuren, twee abonnementen): de uitleg van de server, in het venster.
      setProblem(errorMessage(err) ?? 'Samenvoegen lukte niet. Probeer het opnieuw.');
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const option = (x: CustomerSummary, why?: string) => <li key={x.id} data-id="element-1381" data-mp="29zhwb0xk0wsx">
      <button type="button" onClick={() => {
      setPicked(x);
      setProblem('');
    }} className="flex w-full items-baseline justify-between gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-1382" data-mp="2a12s0tvlulsd">
        <span className="text-[14px] text-ink" data-id="element-1383" data-mp="3lioufk0bnxdh">{customerName(x)}</span>
        <span className="truncate text-[12px] text-muted" data-id="element-1384" data-mp="3gisqfkrmk5xx">{[why, contactHint(x)].filter(Boolean).join(' · ')}</span>
      </button>
    </li>;
  return <dialog ref={ref} aria-labelledby="merge-title" onCancel={e => {
    e.preventDefault();
    if (!busy) onClose();
  }} className="w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-shell p-0 text-ink shadow-xl backdrop:bg-ink/40" data-id="element-1385" data-mp="i0dkwq0g6r1s">
      <div className="p-5" data-id="element-1386" data-mp="3bhib8h5u68k">
        <h2 id="merge-title" className="text-[16px] font-medium text-ink" data-id="element-1387" data-mp="3a6ex9rmn0hz">
          {picked ? `${customerName(picked)} samenvoegen?` : 'Samenvoegen met…'}
        </h2>

        {picked ? <>
            <p className="mt-2 text-[14px] text-muted" data-id="element-1388" data-mp="gioi65rndvqu">
              Alles van {customerName(picked)} komt in het dossier van {customerName(c)}: bestellingen, afspraken, kassabonnen, tegoed, bezoeken, foto's en de notitie.
              Lege contactgegevens hier worden aangevuld. Daarna verdwijnt het andere dossier; dit kun je niet terugdraaien.
            </p>
            {problem && <p role="alert" className="mt-3 text-[13px] text-clay" data-id="element-1389" data-mp="blev2wrqdw66">
                {problem}
              </p>}
            <div className="mt-5 flex flex-wrap justify-end gap-2" data-id="element-1390" data-mp="148r8agc04rmt">
              <button type="button" onClick={() => setPicked(null)} disabled={busy} className={btnGhost} data-id="element-1391" data-mp="14a2bof1jbxde">
                Andere kiezen
              </button>
              <button type="button" onClick={merge} disabled={busy} aria-busy={busy || undefined} className={btnPrimary} data-id="element-1392" data-mp="uh4zeqn4z4n8">
                {busy && <Spinner data-id="element-1393" data-mp="1i5xpi6h2ui9x" />}
                {busy ? 'Samenvoegen…' : 'Samenvoegen'}
              </button>
            </div>
          </> : <>
            <p className="mt-1 text-[14px] text-muted" data-id="element-1394" data-mp="37nuvn087d2xm">Kies het dubbele dossier. Het gaat op in dit dossier van {customerName(c)}.</p>
            <label className="relative mt-4 block" data-id="element-1395" data-mp="32nypovfy31ff">
              <span className="sr-only" data-id="element-1396" data-mp="3ghpszm331p2p">Zoek een klant</span>
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-1397" data-mp="3cfuq4d5i73mt" />
              <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Naam, e-mail of telefoon" className={`${inputCls} pl-9`} data-id="element-1398" data-mp="2oqtop9v38sr1" />
            </label>
            <div className="mt-3 min-h-[6rem]" data-id="element-1399" data-mp="2xs17skp19n0j">
              {status === 'loading' && customers.length === 0 ? <p className="flex items-center gap-2 py-4 text-[14px] text-muted" role="status" data-id="element-1400" data-mp="2sqbyb9hglhek">
                  <Spinner data-id="element-1401" data-mp="cz6854mqhlhx" /> Klanten laden…
                </p> : status === 'error' ? <p role="alert" className="py-4 text-[14px] text-clay" data-id="element-1402" data-mp="814rsrgp00a5">
                  De klanten konden niet laden. Sluit en probeer het opnieuw.
                </p> : query.trim() ? results.length ? <ul data-id="element-1403" data-mp="36ujxqohufz3s">{results.map(x => option(x))}</ul> : <p className="py-4 text-[14px] text-muted" data-id="element-1404" data-mp="3au2k7opbci8q">Niemand gevonden voor “{query.trim()}”.</p> : suggestions.length ? <>
                  <p className="px-3 text-[12px] text-muted" data-id="element-1405" data-mp="2j34rwlquyzns">Mogelijk dezelfde klant</p>
                  <ul className="mt-1" data-id="element-1406" data-mp="2j11v9iv7a8a3">{suggestions.map(d => option(d.customer, d.reasons.map(r => duplicateReasonLabel[r]).join(', ')))}</ul>
                </> : <p className="py-4 text-[14px] text-muted" data-id="element-1407" data-mp="2s2i4voe6y6d9">Geen dossiers die op deze klant lijken. Zoek op naam of telefoon.</p>}
            </div>
            <div className="mt-4 flex justify-end" data-id="element-1408" data-mp="1zfxasg6bzg60">
              <button type="button" onClick={onClose} className={btnGhost} data-id="element-1409" data-mp="1zd2pkmfhtrbj">
                Annuleren
              </button>
            </div>
          </>}
      </div>
    </dialog>;
}